import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Radio } from "@/components/Radio/Radio";

import { useGetCompanyUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import {
	colleagueIdsOf,
	colleagueScopeOf,
	isColleagueScopeActive,
	NotificationThresholds,
	toggleColleagueId,
} from "./notificationThresholds.util";

import type { NotificationGroupDefinition } from "@/pages/Private/pages/Notifications/schema/notification";
import type { CompanyUser } from "@/pages/Private/pages/Users/schema/get-users";

interface ReplyColleagueScopePickerProps {
	group: NotificationGroupDefinition;
	thresholds: NotificationThresholds | null | undefined;
	currentUserId: number | undefined;
	onChange: (patch: NotificationThresholds) => void;
}

const colleagueName = (colleague: CompanyUser): string =>
	[colleague.firstName, colleague.lastName].filter(Boolean).join(" ") || `#${colleague.id}`;

/**
 * "Whose replies" for the Engagement group's "…to colleagues" toggles (PREC-4259): everyone in the
 * organization, or only the colleagues picked here. Stored in the same `thresholds` row as the
 * toggles; the server decides delivery from it, and only for leads the viewer can open.
 */
export const ReplyColleagueScopePicker: FunctionComponent<ReplyColleagueScopePickerProps> = ({
	group,
	thresholds,
	currentUserId,
	onChange,
}) => {
	const { t } = useTranslation();
	const definition = group.colleagueScope;
	const isActive = isColleagueScopeActive(group, thresholds);
	const scope = definition ? colleagueScopeOf(definition, thresholds) : undefined;
	const { data: companyUsers } = useGetCompanyUsersQuery(undefined, {
		skip: !isActive || scope !== "selected",
	});

	if (!definition) {
		return null;
	}

	const selectedIds = colleagueIdsOf(definition, thresholds);
	const colleagues = (companyUsers?.data ?? []).filter((user) => user.id !== currentUserId);

	return (
		<div className="py-1" data-test={`colleague-scope-${group.key}`}>
			<span className="text-xs text-gray-700">{definition.label}</span>
			<p className="text-[11px] text-gray-400 mt-0.5">{definition.description}</p>

			{!isActive ? (
				<p className="text-[11px] text-gray-400 mt-1">
					{t("notifications.preferences.colleagues.inactive")}
				</p>
			) : (
				<div className="mt-2 flex flex-col gap-2">
					{definition.options.map((option) => (
						<Radio
							key={option.value}
							isChecked={scope === option.value}
							testId={`colleague-scope-option-${option.value}`}
							onChange={() => onChange({ [definition.scopeKey]: option.value })}
						>
							<span className="text-xs text-gray-700">{option.label}</span>
						</Radio>
					))}

					{scope === "selected" ? (
						<div className="ml-7 flex flex-col gap-1 max-h-48 overflow-y-auto">
							{colleagues.map((colleague) => (
								<Checkbox
									key={colleague.id}
									isChecked={selectedIds.includes(colleague.id)}
									testId={`colleague-${colleague.id}`}
									onChange={() =>
										onChange({
											[definition.idsKey]: toggleColleagueId(selectedIds, colleague.id),
										})
									}
								>
									<span className="text-xs text-gray-700">{colleagueName(colleague)}</span>
								</Checkbox>
							))}

							{selectedIds.length === 0 ? (
								<p className="text-[11px] text-status-warning">
									{t("notifications.preferences.colleagues.noneSelected")}
								</p>
							) : null}
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};
